import { Area } from '@Domain/model/area';
import { Provider } from '@Domain/model/provider';
import { InsuranceType } from '@Framework/constants/InsuranceType';
import { Box, createStyles, makeStyles, SvgIconProps, Theme, Typography } from '@material-ui/core';
import { RequireContext } from '@storybook/core';
import { CORPORATE_COLORS } from '@Ui/theme/colors/corporate';
import React, { ReactNode } from 'react';

import { Badge } from '../../atoms/Badge/Badge';
import { Link } from '../../atoms/Link';
import { Modal } from '../../atoms/Modal/Modal';
import { Text } from '../../atoms/Text';
import { CollapsibleList } from '../../molecules/CollapsibleList/CollapsibleList';

const importAll = (r: RequireContext) => {
  const images = {};
  r.keys().forEach((item: string) => {
    images[item.replace('./', '')] = r(item).default;
  });

  return images;
};

export const dynamicIcons = importAll(require.context('../../assets/logo/', false, /\.svg$/));
type CustomSvgIconProps = Omit<SvgIconProps, 'component'> & {
  icon: string;
  className: string;
  alt: string;
  width: string;
};

const CustomSvgIcon = ({ icon, className, alt, width }: CustomSvgIconProps) => (
  <img src={dynamicIcons[`${icon}`]} className={className} alt={alt} width={width} />
);

const contentProvider: React.FC<ProviderModalProps> = props => {
  const { provider, area, insuranceType, trigger } = props;
  const listItems = () => {
    const faqArea = provider.faq?.[area];

    return faqArea?.map(item => {
      return {
        collapsibleChildren: (
          <Typography style={{ whiteSpace: 'pre-wrap' }} dangerouslySetInnerHTML={{ __html: item.description }} />
        ),
        isCollapsible: true,
        text: <Text text={item.title} isBold />
      };
    });
  };

  const useStyles = makeStyles((theme: Theme) =>
    createStyles({
      root: {
        padding: '1rem',
        marginTop: '2rem',
        position: 'relative',
        backgroundColor: 'white',
        border: '0.1rem solid',
        borderColor: 'white'
      },
      border: {
        borderWidth: '0 0 0 0.1rem',
        border: '0.1rem solid',
        borderColor: CORPORATE_COLORS.GRAY,
        borderRadius: '0.4rem',
        fontSize: '0.75rem',
        padding: '0 0.4rem 0 0',
        overflow: 'hidden',
        position: 'absolute',
        top: '-1.2rem'
      },
      skrewRightBorderInner: {
        marginLeft: '-0.5rem',
        transform: 'skew(-20deg)',
        borderColor: CORPORATE_COLORS.GRAY,
        padding: '0.5rem',
        border: '0.1rem solid',
        backgroundColor: 'white',
        borderRadius: '0.4rem'
      },
      skrewRightBorderLabel: {
        color: CORPORATE_COLORS.GRAY,
        marginLeft: '0.5rem',
        transform: 'skew(20deg)',
        fontSize: '10px',
        fontFamily: ['"New Brand"', '"Helvetica Neue"', '"Nimbus Sans L"', 'sans-serif'].join(',')
      },
      providerLogo: {
        paddingTop: '1rem'
      },
      paddingTop: {
        paddingTop: '1rem'
      },
      subtitle2: {
        paddingBottom: '0.5rem'
      },
      trustBadge: {
        margin: '1.0rem',
        display: 'inline-block'
      }
    })
  );

  const classes = useStyles();
  return (
    <>
      <CustomSvgIcon icon={provider.logo} className={classes.providerLogo} width="100" alt={provider.name} />
      <Typography variant="subtitle1">{provider.slogan}</Typography>

      <Box className={classes.root}>
        {provider.isPremiumpartner && (
          <div className={classes.border}>
            <div className={classes.skrewRightBorderInner}>
              <div className={classes.skrewRightBorderLabel}>SHOP APOTHEKE Partner</div>
            </div>
          </div>
        )}
        <Box>
          <Typography variant="subtitle2" className={classes.subtitle2}>
            Ihre Kundenvorteile:
          </Typography>
          {provider.benefits?.[area]?.[insuranceType]?.map(({ label }, index) => (
            <Badge key={'badge' + index} {...{ benefits: true, label }} />
          ))}
          {provider.description?.[area]?.map((paragraph, id) => (
            <Typography key={id} variant="body1" className={classes.paddingTop}>
              {paragraph}
            </Typography>
          ))}
        </Box>
      </Box>
      <Box pt={2.5} pb={2.5}>
        <CollapsibleList listItems={listItems() ?? []} />
      </Box>
      <Box textAlign="center">
        <CustomSvgIcon icon="logo-trusted-shops.svg" className={classes.trustBadge} width="50" alt="TRUSTED SHOPS" />
        <CustomSvgIcon icon="logo-tuv-nord.svg" className={classes.trustBadge} width="50" alt="TÜV Nord" />
      </Box>
    </>
  );
};

export function ProviderModal(props: ProviderModalProps) {
  return (
    <>
      <Modal
        title="Anbieterinformationen"
        trigger={props.trigger}
        linkText={`Mehr Informationen zu ${props.provider.name}`}
      >
        {contentProvider(props)}
      </Modal>
    </>
  );
}

export interface ProviderModalProps {
  provider: Provider;
  trigger: React.ReactNode;
  area: Area;
  insuranceType: InsuranceType;
}
